import { Dialog } from '@angular/cdk/dialog';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs';
import { LicenseBatchDto } from 'src/app/models/license.model';
import { readsDataAcrossCompanies, UserRole } from 'src/app/models/user.model';
import { LicenseBatchStore } from 'src/app/services/license-batch.store';
import { selectProfileState } from 'src/app/stores/profile/profile.selector';
import { matchesSearch } from 'src/app/utils/text-search';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';

@Component({
  selector: 'app-license-batch-list',
  templateUrl: './license-batch-list.component.html',
  styleUrls: ['./license-batch-list.component.scss'],
  standalone: false,
})
export class LicenseBatchListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly licenseBatchStore = inject(LicenseBatchStore);
  private readonly profile = toSignal(this.store.select(selectProfileState), {
    requireSync: false,
  });

  searchText = '';

  ngOnInit(): void {
    this.loadBatches();
  }

  get filteredBatches(): LicenseBatchDto[] {
    return this.licenseBatchStore
      .batches()
      .filter((batch) =>
        matchesSearch(this.searchText, [
          batch.id,
          batch.name,
          batch.description,
          batch.companyId,
          batch.company?.name,
          batch.licensedProductId,
          batch.product?.name,
          batch.product?.target,
        ]),
      );
  }

  onSearchChange(searchText: string): void {
    this.searchText = searchText;
  }

  get isMasterAdmin(): boolean {
    return this.profile()?.isMasteradmin ?? false;
  }

  /**
   * Whether this user reaches every batch rather than only their own company's, and so belongs on
   * the administrative routes. A support user does, without any of the edit actions.
   */
  get readsAllBatches(): boolean {
    return readsDataAcrossCompanies(this.profile());
  }

  get canManageLicenseAssignments(): boolean {
    const profile = this.profile();
    return profile?.isMasteradmin === true || profile?.role === UserRole.ADMIN;
  }

  get isAdminRoute(): boolean {
    return this.router.url.includes('/admin/');
  }

  navigateToCreateBatch(): void {
    if (!this.isMasterAdmin) {
      return;
    }
    this.router.navigate(['/admin/licenses/batches/create']);
  }

  navigateToBatchDetails(batchId: string): void {
    if (!this.readsAllBatches) {
      this.router.navigate(['/licenses/batches', batchId]);
      return;
    }
    this.router.navigate(['/admin/licenses/batches', batchId]);
  }

  navigateToEditBatch(batchId: string): void {
    if (!this.isMasterAdmin) {
      this.navigateToBatchDetails(batchId);
      return;
    }
    this.router.navigate(['/admin/licenses/batches', batchId, 'edit']);
  }

  navigateToManageDevices(batchId: string): void {
    if (!this.readsAllBatches) {
      this.router.navigate(['/licenses/batches', batchId, 'devices']);
      return;
    }
    this.router.navigate(['/admin/licenses/batches', batchId, 'devices']);
  }

  navigateToManageUsers(batchId: string): void {
    const base = this.readsAllBatches
      ? '/admin/licenses/batches'
      : '/licenses/batches';
    this.router.navigate([base, batchId, 'users']);
  }

  deleteBatch(batchId: string): void {
    if (!this.isMasterAdmin) {
      return;
    }
    const batch =
      this.licenseBatchStore
        .batches()
        .find((existingBatch) => existingBatch.id === batchId) ?? null;
    const batchLabel = batch?.name?.trim() || batch?.id || 'this license batch';

    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Delete License Batch',
        bodyText: `Are you sure you want to delete "${batchLabel}"?\n\nThis action cannot be undone.`,
        cancelable: true,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.licenseBatchStore.deleteBatch(batchId);
      }
    });
  }

  getErrorMessage(error: unknown): string {
    if (!error) {
      return '';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as { message?: string };
      if (typedError.message) {
        return typedError.message;
      }
    }
    return 'Failed to load license batches.';
  }

  trackByBatch(index: number, batch: LicenseBatchDto): string {
    return (
      batch.id ??
      `${batch.name ?? batch.licensedProductId ?? 'license-batch'}-${index}`
    );
  }

  private loadBatches(): void {
    if (this.isAdminRoute) {
      this.licenseBatchStore.getAllBatches('BRIEF');
      return;
    }

    this.store
      .select(selectProfileState)
      .pipe(
        map((profile) => profile.companyId?.trim() || ''),
        filter((companyId): companyId is string => !!companyId),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((companyId) => {
        this.licenseBatchStore.getAllBatchesByCompany({
          companyId,
          projection: 'BRIEF',
        });
      });
  }
}
